import { jsx, Fragment } from 'react/jsx-runtime';
import { useSyncExternalStore, Suspense, useMemo, cloneElement } from 'react';
import { Outlet, useMatches, useLocation, useNavigate, createBrowserRouter, RouterProvider, Link } from 'react-router-dom';
import KeepAlive, { AliveScope } from 'react-activation';

function traverse(list, callback, parent) {
    list.forEach((v) => {
        callback(v, parent);
        if (Array.isArray(v.children))
            traverse(v.children, callback, v);
    });
}
function next(callback, val) {
    return Promise.resolve(val).then(callback);
}

function subscribe(initVal) {
    let state = initVal;
    const dep = new Set();
    const getState = () => state;
    const setState = (nextState) => {
        const next = typeof nextState === 'function' ? nextState(state) : nextState;
        if (!Object.is(state, next)) {
            state = next;
            dep.forEach((render) => render());
        }
    };
    const useScribe = Object.assign(() => {
        // const [, setVal] = useState(0);
        // useEffect(() => {
        //     const render = () => setVal(Math.random());
        //     dep.add(render);
        //     return () => dep.delete(render)
        // }, []);
        // return state;
        return useSyncExternalStore((render) => {
            dep.add(render);
            return () => dep.delete(render);
        }, () => state);
    }, { getState, setState });
    return useScribe;
}

//
function toPath(loc) {
    return typeof loc === 'string' ? loc : loc.pathname + loc.search + loc.hash;
}
function createHistory(max = 6) {
    const history = [];
    const keep = () => {
        while (history.length > max) {
            history.shift();
        }
    };
    // 保存
    const push = (loc) => {
        const last = history[history.length - 1] ?? {};
        // bad
        if (JSON.stringify(last) !== JSON.stringify(loc)) {
            history.push(loc);
            keep();
            return loc;
        }
    };
    // 取 from,to
    const buddy = () => [null, null, ...history].slice(-2);
    return {
        push,
        buddy
    };
}

const createBefore = () => {
    const beforeDep = new Set();
    const beforeRoute = (to, from) => {
        return [...beforeDep].reduce((queue, before) => queue.then(() => before(to, from)), Promise.resolve(undefined));
    };
    return { beforeDep, beforeRoute };
};
const createAfter = () => {
    const afterDep = new Set();
    const afterRoute = (route, record) => {
        [...afterDep].forEach((after) => after(route, record));
    };
    return { afterDep, afterRoute };
};

/** global var */
const router = subscribe(null);
const history = createHistory();
const { beforeDep, beforeRoute } = createBefore();
const { afterDep, afterRoute } = createAfter();
const routesMap = new Map();
/** components */
function Skeleton({ children, fallback = jsx(Fragment, {}) }) {
    return children ? (jsx(Suspense, { fallback: fallback, children: children })) : (jsx(Fragment, { children: fallback }));
}
function NestedRoute() {
    const record = router();
    // 只有路由改变才重新渲染
    const memo = useMemo(() => (record ? jsx(Guard, { record: record }) : jsx(Fragment, {})), [record]);
    return memo;
}
/** 拦截、骨架屏、钩子 ... */
function Guard({ record }) {
    const { step, from, to, nav, result } = record;
    // lazy组件会重复渲染，需要将step重新置为0，目前是兼容方案
    const nextId = to.matches[step.size]?.id;
    // 未匹配到路由
    if (!nextId)
        return (jsx(Skeleton, { skeleton: to?.route?.skeleton, children: jsx(Outlet, {}) }));
    const prevId = from?.matches[step.size]?.id;
    const route = routesMap.get(nextId);
    const { children, skeleton, redirect, before, after } = route;
    // trick:自增匹配下一层级
    record.step.add(nextId);
    // result只能是true的情况下执行after钩子
    if (result === true && to.matches.length === step.size) {
        // todo:如何处理第一次异步组件加载重新渲染的问题
        afterRoute(to, from);
        next(() => currentRoute.setState(to));
    }
    // 该层级路由未改变
    if (prevId === nextId) {
        return (jsx(Skeleton, { fallback: skeleton, children: jsx(Outlet, {}) }));
    }
    // before已完成
    if (result !== undefined) {
        // todo: 执行内部钩子
        /** 拦截情况 */
        if (result === false && from) {
            nav(toPath(from), { replace: true });
            return jsx(Skeleton, { fallback: skeleton });
        }
        if (typeof result === "string" || typeof result === "object") {
            // 确保重定向和to不是同一个路由
            const [next, redirect] = [toPath(to), toPath(result)];
            if (next !== redirect) {
                nav(redirect, { replace: true });
                return jsx(Skeleton, { fallback: skeleton });
            }
        }
        /** situation end */
        return (
        // todo:过渡动画 先执行prevId的关闭动画，动画完成再渲染outLet
        jsx(Skeleton, { fallback: skeleton, children: jsx(Outlet, {}) }));
    }
    // before未完成
    return jsx(Skeleton, { fallback: skeleton });
}
function Root() {
    // slice是为了root本身不取
    const matches = useMatches().slice(1);
    const route = routesMap.get(matches[matches.length - 1]?.id);
    const { key, ...rest } = useLocation();
    const _nav = useNavigate();
    // 跳转不能同步执行
    const nav = (path, option) => next(() => _nav(path, option));
    // 记录下
    const newone = history.push(Object.assign(rest, { matches, route, meta: route?.meta ?? {} }));
    // 同路由跳转不记录
    if (newone) {
        let result;
        const [from, to] = history.buddy();
        // 重定向则不再继续
        const { redirect } = route;
        if (redirect) {
            requestAnimationFrame(() => {
                nav(redirect, { replace: true });
            });
        }
        else {
            const before = beforeRoute(to, from).then((res) => {
                // 即如果是undefined、null表示不会拦截
                result = res ?? true;
                return result;
            });
            // 等待before一帧，假装是同步
            requestAnimationFrame(async () => {
                const record = { to, from, nav };
                router.setState({
                    step: new Set(),
                    result,
                    ...record,
                });
                // 如果before没完成，那完成后再渲染一次
                if (result === undefined) {
                    await before;
                    router.setState({
                        step: new Set(),
                        result,
                        ...record,
                    });
                }
            });
        }
    }
    return (jsx(AliveScope, { children: jsx(NestedRoute, {}) }));
}
function createRouter(routeList, rootOptions) {
    //
    const { inherit = [], ...options } = rootOptions;
    const root = [
        {
            path: "",
            id: "_ROOT_",
            component: () => jsx(Root, {}),
            children: routeList,
            ...options,
        },
    ];
    // todo:优化update
    const metaNames = inherit
        .filter((v) => v.includes("meta."))
        .map((v) => v.replace("meta.", ""));
    // 1. 设置route的element属性 2. 保存到map 3. 继承
    traverse(root, (v, parent) => {
        // component转element
        if (!v.element && v.component) {
            const Comp = v.component;
            v.element = jsx(Comp, {});
            //
            delete v.component;
        }
        // inherit
        inherit.map((propname) => {
            v[propname] = v[propname] ?? parent?.[propname];
        });
        // metaNames
        metaNames.map((metaname) => {
            if (!v.meta)
                v.meta = {};
            const meta = v.meta;
            meta[metaname] = meta[metaname] ?? parent?.meta[metaname];
        });
        // 传值
        const { element, ...props } = v;
        v.element = cloneElement(v.element, props);
        //
        if (v.keepalive) {
            v.element = jsx(KeepAlive, { cacheKey: v.id, children: v.element });
        }
        routesMap.set(v.id, v);
    });
    const router = Object.assign(createBrowserRouter(root, {}), {
        before: beforeDep.add.bind(beforeDep),
        after: afterDep.add.bind(afterDep),
        RouterProvider: () => jsx(RouterProvider, { router: router }),
    });
    return router;
}
const currentRoute = subscribe();
const jumpTo = subscribe();
function RouteLink(props) {
    const { id } = props;
    return jsx(Link, { ...props, onClick: () => jumpTo.setState(id) });
}

export { NestedRoute, Root, RouteLink, Skeleton, currentRoute, createRouter as default, jumpTo, router, routesMap, subscribe, toPath };
